import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldValues } from 'react-hook-form';

import { useRequest } from '@common/services/api';
import { useAppSelector } from '@common/hooks';
import Spinner from '@common/components/spinner';
import { Button } from '@common/components/button';

import { selected } from '@modules/organisation/selectors/organisation';
import { CompanyValue, CompanyValuePayload } from '@modules/admin/types';
import { createCompanyValue, updateCompanyValue, deleteCompanyValue } from '@modules/admin/api/company-values';
import CompanyValueForm from './company-value-form';
import CompanyValueActions from './company-value-actions';
import CompanyValueEmpty from './company-value-empty';
import * as Alert from '@common/services/alert';

export const CompanyValues: React.FC = () => {
  const { t } = useTranslation();
  const [showAddValueForm, setShowAddValueForm] = useState(false);
  const [editCompanyValue, setEditCompanyValue] = useState<CompanyValue | null>(null);

  const organisation = useAppSelector(selected);

  const {
    data: values,
    setData: setValues,
    loading,
  } = useRequest<{ data: CompanyValue[]; meta: null }>(`/v1/organisations/${organisation.id}/values`);

  const toggleShowAddValueForm = () => {
    setShowAddValueForm(!showAddValueForm);
  };

  const onSave = async (data: FieldValues) => {
    try {
      const payload: CompanyValuePayload = {
        name: data.title,
        description: data.description,
      };
      if (editCompanyValue) {
        const res = await updateCompanyValue(organisation.id, editCompanyValue.id, payload);
        setValues((prev) => prev.map((value) => (value.id === editCompanyValue.id ? res.data : value)));
        Alert.success(t('organisation:company_value_updated'));
      } else {
        const res = await createCompanyValue(organisation.id, payload);
        setValues((prev) => [...prev, res.data]);
        Alert.success(t('organisation:company_value_created'));
      }
      handleClose();
    } catch (error) {
      Alert.error(t(editCompanyValue ? 'organisation:company_value_update_error' : 'organisation:company_value_create_error'));
    }
  };

  const handleDelete = async (value: CompanyValue) => {
    try {
      await deleteCompanyValue(organisation.id, value.id);
      setValues((prev) => prev.filter((v) => v.id !== value.id));
      Alert.success(t('organisation:company_value_deleted'));
    } catch (error) {
      Alert.error(t('organisation:company_value_delete_error'));
    }
  };

  const triggerEdit = (value: CompanyValue) => {
    setEditCompanyValue(value);
    toggleShowAddValueForm();
  };

  const handleClose = () => {
    setShowAddValueForm(false);
    setEditCompanyValue(null);
  };

  return (
    <div className="company-values">
      {loading && <Spinner />}

      {(values.length === 0 && !loading) && <CompanyValueEmpty />}

      {values.map((value) => (
        <div
          key={value.id}
          className="tw-flex tw-justify-between tw-items-start tw-py-3
          tw-border-b tw-border-x-0 tw-border-t-0 tw-border-gray-200 tw-border-solid"
        >
          <div className="tw-flex-1">
            <div className="tw-font-semibold tw-mb-1">{value.name}</div>
            <div className="tw-text-gray-500">{value.description}</div>
          </div>
          <CompanyValueActions
            onEdit={() => triggerEdit(value)}
            onDelete={() => handleDelete(value)}
          />
        </div>
      ))}

      <div className="tw-mt-4">
        <Button
          onClick={toggleShowAddValueForm}
          icon="add"
          type="inverted-primary"
        >
          {t('organisation:add_company_value_button')}
        </Button>
      </div>

      {showAddValueForm && (
        <CompanyValueForm
          onSave={onSave}
          isVisible={showAddValueForm}
          editCompanyValue={editCompanyValue}
          handleClose={handleClose}
        />
      )}
    </div>

  );
};

export default CompanyValues;
